import { useEffect, useState } from 'react';
import { getStreakCalendar, registerActivity } from '../../api/quiz.api';
import { useAuth } from '../../context/AuthContext';
import { isStudiedToday } from '../../utils/streak';
import { celebrate } from '../brand/celebrate';

/* Finishing a session is the moment to celebrate the streak — but only when
   this session is what earned the day. Answering a card already registers
   the streak server-side, so by the time a session ends the server always
   says "studied today"; asking then would celebrate on every visit.

   So the question is asked *once on arrival*, before any answer lands, and
   the answer to that is what decides whether finishing is worth a screen.

   Extracted from Kana, which was the only page that had any of this. Every
   other exercise — vocab flashcards, all three kanji modes, the JLPT quiz,
   grammar lessons — ended with no celebration at all, which is why a
   session finished anywhere else went by in silence.

   Returns { celebration, dismiss }; render <StreakCelebration> when
   `celebration` is set. */
export default function useStreakCelebration(sessionJustFinished) {
  const { updateUser } = useAuth();
  const [doneToday, setDoneToday] = useState(null);   // null = not looked up yet
  const [celebration, setCelebration] = useState(null); // { streak, lastStudied }

  useEffect(() => {
    let alive = true;
    getStreakCalendar()
      .then(res => { if (alive) setDoneToday(isStudiedToday(res.data.lastStudied)); })
      // On failure stay quiet rather than celebrate wrongly.
      .catch(() => { if (alive) setDoneToday(true); });
    return () => { alive = false; };
  }, []);

  /* The mark in the sidebar reacts to every finish, streak or not. */
  useEffect(() => {
    if (sessionJustFinished) celebrate();
  }, [sessionJustFinished]);

  /* Finishing is what banks the day. The graded-content endpoints used to
     do it per card, which meant answering one flashcard and then leaving
     still counted as a day studied — the streak recorded that you opened
     something rather than that you did it. They no longer touch the
     streak, so the completion has to say so explicitly. */
  useEffect(() => {
    if (!sessionJustFinished || doneToday !== false) return;
    // Claim it immediately so a second session cannot re-trigger the screen.
    setDoneToday(true);
    registerActivity()
      .then(() => getStreakCalendar())
      .then(res => {
        setCelebration({ streak: res.data.streak, lastStudied: res.data.lastStudied });
        // Light up the header pill without waiting for a route change.
        updateUser({ streak: res.data.streak, lastStudied: res.data.lastStudied });
      })
      .catch(() => {});
  }, [sessionJustFinished, doneToday, updateUser]);

  return { celebration, dismiss: () => setCelebration(null) };
}
